import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { ProductsService } from '../../../api';
import { useDebounce } from '../../../hooks/useDebounce';

export interface ProductOption {
  id: number;
  name: string;
  code: string;
}

const MAX_PRODUCTS = 4;
const ITEMS_PER_PAGE = 20;

export interface ProductSearchSelectProps {
  /** IDs de productos seleccionados (1-4) */
  value: number[];
  /** Callback al cambiar la selección */
  onChange: (ids: number[]) => void;
  /** Deshabilitado */
  disabled?: boolean;
  /** Placeholder del input */
  placeholder?: string;
  /** Estilos adicionales */
  sx?: object;
}

/**
 * Selector de productos con búsqueda por código o nombre.
 * Permite seleccionar hasta 4 productos para la gráfica de tendencia.
 */
const ProductSearchSelect: React.FC<ProductSearchSelectProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Buscar por código o nombre...',
  sx,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [productCache, setProductCache] = useState<Map<number, ProductOption>>(() => new Map());
  const debouncedSearch = useDebounce(searchInput, 300);

  const selectedIds = useMemo(() => value ?? [], [value]);

  // Fetch products by search term
  const fetchProducts = useCallback(
    async (search: string) => {
      if (disabled) return;
      setLoading(true);
      try {
        const response = await ProductsService.productsControllerFindAll(
          1,
          ITEMS_PER_PAGE,
          undefined,
          undefined,
          search.trim() || undefined,
          undefined,
          'ACTIVE',
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
        );
        const data = response?.data ?? response;
        const list = Array.isArray(data) ? data : [];
        const options: ProductOption[] = list.map(
          (item: { id: number; name: string; code: string }) => ({
            id: item.id,
            name: item.name ?? '',
            code: item.code ?? '',
          }),
        );
        setProducts(options);
        setProductCache((prev) => {
          const next = new Map(prev);
          options.forEach((p) => next.set(p.id, p));
          return next;
        });
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    },
    [disabled],
  );

  useEffect(() => {
    fetchProducts(debouncedSearch);
  }, [debouncedSearch, fetchProducts]);

  // Fetch product details for ids we don't have in cache (e.g. from URL/persist)
  useEffect(() => {
    if (selectedIds.length === 0) return;
    const missing = selectedIds.filter((id) => !productCache.has(id));
    if (missing.length === 0) return;
    Promise.all(
      missing.map((id) => ProductsService.productsControllerFindOne(id).catch(() => null)),
    ).then((results) => {
      const toAdd: ProductOption[] = [];
      for (const r of results) {
        if (r != null) {
          toAdd.push({ id: r.id, name: r.name ?? '', code: r.code ?? '' });
        }
      }
      if (toAdd.length > 0) {
        setProductCache((prev) => {
          const next = new Map(prev);
          toAdd.forEach((p) => next.set(p.id, p));
          return next;
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join(',')]);

  const selectedProducts = useMemo(() => {
    return selectedIds
      .map((id) => productCache.get(id))
      .filter((p): p is ProductOption => p != null);
  }, [selectedIds, productCache]);

  const availableOptions = useMemo(() => {
    const idsSet = new Set(selectedIds);
    return products.filter((p) => !idsSet.has(p.id));
  }, [products, selectedIds]);

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: ProductOption[]) => {
      const limited = newValue.slice(0, MAX_PRODUCTS);
      setProductCache((prev) => {
        const next = new Map(prev);
        limited.forEach((p) => next.set(p.id, p));
        return next;
      });
      onChange(limited.map((p) => p.id));
    },
    [onChange],
  );

  const getOptionLabel = useCallback((option: ProductOption) => {
    return `${option.name} (${option.code})`;
  }, []);

  const isOptionEqualToValue = useCallback((option: ProductOption, val: ProductOption) => {
    return option.id === val.id;
  }, []);

  return (
    <Autocomplete<ProductOption, true>
      multiple
      value={selectedProducts}
      onChange={handleChange}
      inputValue={searchInput}
      onInputChange={(_, newInput, reason) => {
        if (reason === 'input') setSearchInput(newInput);
        else if (reason === 'clear') setSearchInput('');
      }}
      options={availableOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      loading={loading}
      disabled={disabled}
      sx={{ minWidth: 280, ...sx }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            selectedProducts.length >= MAX_PRODUCTS
              ? `Máximo ${MAX_PRODUCTS} productos`
              : placeholder
          }
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ProductSearchSelect;
